import "@/App.css";
import { useState, useRef, useCallback } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { apiErrorMessage } from "@/lib/api";
import {
  composeWorkspaceName,
  normalizeWorkspacePart,
  splitWorkspaceName,
} from "@/lib/workspaceName";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useCustomization } from "@/context/CustomizationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import ExportButton from "@/components/common/ExportButton";
import { toast } from "sonner";
import {
  LayoutDashboard, TableProperties, ListOrdered, Briefcase, Tags, SlidersHorizontal,
  Menu, LogOut, ChevronsUpDown, Check, Plus, Loader2,
  PanelLeftClose, PanelLeftOpen, History, Settings, ShieldAlert, Pencil, Upload, X,
  AlertTriangle,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────────────────

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const validateLogoFile = (file) => {
  if (!file.type.startsWith("image/")) {
    toast.error("Please select an image file (PNG, JPG, SVG, …).");
    return false;
  }
  if (file.size > 1_048_576) {
    toast.error("Logo must be smaller than 1 MB.");
    return false;
  }
  return true;
};

// ── Shared sub-components ──────────────────────────────────────────────────

/** Renders a workspace logo image, or a 2-letter abbreviation fallback. */
const WsLogo = ({ workspace, size = "sm" }) => {
  const cls = size === "lg"
    ? "h-10 w-10 rounded-lg"
    : size === "md"
    ? "h-8 w-8 rounded-md"
    : "h-6 w-6 rounded-md";

  const abbr = (workspace?.name || "??").slice(0, 2).toUpperCase();

  if (workspace?.logo) {
    return (
      <img
        src={workspace.logo}
        alt={workspace.name}
        className={`${cls} object-contain bg-white border border-border shrink-0`}
      />
    );
  }
  return (
    <div
      className={`${cls} bg-teal-50 border border-teal-100 dark:bg-teal-950/50 dark:border-teal-900 flex items-center justify-center shrink-0`}
    >
      <span className="text-[10px] font-bold text-teal-700 dark:text-teal-300 leading-none">
        {abbr}
      </span>
    </div>
  );
};

/** A small logo-upload area used in both Create and Edit dialogs. */
const LogoUpload = ({ logo, onChange, inputRef }) => (
  <div className="space-y-1.5">
    <Label>Logo <span className="text-muted-foreground font-normal">(optional)</span></Label>
    <div className="flex items-center gap-3">
      {logo ? (
        <div className="relative h-14 w-14 shrink-0">
          <img
            src={logo}
            alt="workspace logo preview"
            className="h-14 w-14 rounded-lg object-contain bg-white border border-border"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:opacity-90"
            aria-label="Remove logo"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
      ) : (
        <div className="h-14 w-14 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
          <Upload className="h-5 w-5" />
        </div>
      )}
      <div className="flex flex-col gap-1.5 min-w-0">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (!validateLogoFile(file)) { e.target.value = ""; return; }
            onChange(await toBase64(file));
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          {logo ? "Change logo" : "Upload logo"}
        </Button>
        <p className="text-xs text-muted-foreground">PNG, JPG or SVG · max 1 MB</p>
      </div>
    </div>
  </div>
);

// ── Nav ────────────────────────────────────────────────────────────────────

/** CXO gets the same management access as Admin (user & workspace management). */
const MANAGEMENT_ROLES = ["super_admin", "cxo"];
const CUSTOMIZATION_ROLES = ["super_admin", "cxo", "strategy"];
const isAdminRole = (role) => MANAGEMENT_ROLES.includes(role);
const canCustomize = (role) => CUSTOMIZATION_ROLES.includes(role);

const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/services": "Services & Pricing",
  "/control-panel": "Control Panel",
  "/settings": "Settings",
  "/audit": "Audit Trail",
};

// ── WorkspaceSwitcher ──────────────────────────────────────────────────────

const WorkspaceSwitcher = ({ onCreateClick, onEditClick, collapsed }) => {
  const { workspaces, current, switchWorkspace } = useWorkspace();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="workspace-switcher"
          title={collapsed ? `${current.name} — switch workspace` : undefined}
          className={`flex w-full border-b hover:bg-muted/60 transition-[background-color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--ring))] ${
            collapsed
              ? "items-center justify-center h-[var(--app-topbar-h)] px-2"
              : "flex-col items-center justify-center gap-1.5 py-4 px-3 min-h-[var(--app-topbar-h)]"
          }`}
        >
          {collapsed ? (
            <WsLogo workspace={current} size="md" />
          ) : (
            <>
              <WsLogo workspace={current} size="lg" />
              <div className="flex items-center gap-1 max-w-full">
                <p
                  className="text-sm font-bold text-foreground truncate max-w-[10rem]"
                  data-testid="current-workspace-name"
                >
                  {current.name}
                </p>
                <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
              </div>
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
          Workspaces
        </DropdownMenuLabel>

        {workspaces.map((w) => (
          <DropdownMenuItem
            key={w.id}
            data-testid={`workspace-option-${w.id}`}
            onClick={() => switchWorkspace(w.id)}
            className="cursor-pointer"
          >
            <WsLogo workspace={w} size="sm" />
            <span className="flex-1 truncate ml-2">{w.name}</span>
            {w.id === current.id && <Check className="h-4 w-4 text-[hsl(var(--primary))]" />}
          </DropdownMenuItem>
        ))}

        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="edit-workspace-button"
              onClick={onEditClick}
              className="cursor-pointer"
            >
              <Pencil className="h-4 w-4 mr-2" /> Edit current workspace
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="create-workspace-button"
              onClick={onCreateClick}
              className="cursor-pointer text-[hsl(var(--primary))] focus:text-[hsl(var(--primary))]"
            >
              <Plus className="h-4 w-4 mr-2" /> Create new workspace
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ── SidebarContent ─────────────────────────────────────────────────────────

const SidebarContent = ({ onNavigate, onCreateClick, onEditClick, collapsed = false }) => {
  const { user } = useAuth();
  const { sectionLabels } = useCustomization();
  const isAdmin = isAdminRole(user?.role);
  const isSuperAdmin = user?.role === "super_admin";
  const sectionName = (key, fallback) => sectionLabels?.[key] || fallback;
  const baseNav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-nav-dashboard" },
    { to: "/revenue-summary", label: sectionName("revenue_summary", "Revenue Summary"), icon: TableProperties, testId: "sidebar-nav-revenue-summary" },
    { to: "/revenue-details", label: sectionName("revenue_details", "Revenue Details"), icon: ListOrdered, testId: "sidebar-nav-revenue-details" },
    { to: "/business-tracker", label: sectionName("business_tracker", "Business Tracker"), icon: Briefcase, testId: "sidebar-nav-business-tracker" },
  ];
  const navItems = [
    ...baseNav,
    ...(isAdmin ? [{ to: "/services", label: "Services", icon: Tags, testId: "sidebar-nav-services" }] : []),
    ...(canCustomize(user?.role) ? [{ to: "/control-panel", label: "Control Panel", icon: SlidersHorizontal, testId: "sidebar-nav-control-panel" }] : []),
    ...(isSuperAdmin ? [{ to: "/audit", label: "Audit Trail", icon: History, testId: "sidebar-nav-audit" }] : []),
    { to: "/settings", label: "Settings", icon: Settings, testId: "sidebar-nav-settings" },
  ];
  return (
    <div className="flex flex-col h-full">
      <WorkspaceSwitcher
        onCreateClick={onCreateClick}
        onEditClick={onEditClick}
        collapsed={collapsed}
      />
      <nav className={`flex-1 py-4 space-y-1 ${collapsed ? "px-2" : "px-3"}`}>
        {navItems.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testId}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              `flex items-center rounded-lg text-sm font-medium transition-[background-color,color] duration-150 border-l-2 ${
                collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
              } ${
                isActive
                  ? "bg-teal-50 dark:bg-teal-950/40 text-foreground border-[hsl(var(--primary))]"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60 border-transparent"
              }`
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            {!collapsed && label}
          </NavLink>
        ))}
      </nav>
      <div className={`border-t flex items-center justify-center ${collapsed ? "px-2 py-3" : "px-5 py-4"}`}>
        {collapsed ? (
          <a
            href="https://globalhealthcareresource.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Global Healthcare Resource website (opens in a new tab)"
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
          >
            <div className="bg-white rounded-full p-0.5">
              <img
                src="/global-globe.png"
                alt="Global Healthcare Resource"
                title="Global Healthcare Resource"
                className="h-8 w-8 rounded-full object-contain"
                data-testid="sidebar-logo-collapsed"
              />
            </div>
          </a>
        ) : (
          <a
            href="https://globalhealthcareresource.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Global Healthcare Resource website (opens in a new tab)"
            className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
          >
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img
                src="/global-logo.png"
                alt="Global Healthcare Resource"
                className="h-9 w-auto object-contain"
                data-testid="sidebar-logo"
              />
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

// ── Route guards ───────────────────────────────────────────────────────────

const FullPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--primary))]" />
  </div>
);

// ── AppShell ───────────────────────────────────────────────────────────────

export default function AppShell() {
  const { user, logout } = useAuth();
  const { sectionLabels, error: customizationError, loading: customizationLoading, refresh: refreshCustomization } = useCustomization();
  const {
    current, createWorkspace, updateWorkspace, workspaces, loaded,
    defaultWorkspaceId,
  } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();

  // Mobile / collapse state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");

  // Create workspace dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [wsTeamCode, setWsTeamCode] = useState("");
  const [wsClientName, setWsClientName] = useState("");
  const [wsLogo, setWsLogo] = useState(null);
  const [createAsDefault, setCreateAsDefault] = useState(false);
  const [creating, setCreating] = useState(false);
  const createLogoRef = useRef(null);

  // Edit workspace dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editWsTeamCode, setEditWsTeamCode] = useState("");
  const [editWsClientName, setEditWsClientName] = useState("");
  const [editWsLogo, setEditWsLogo] = useState(null);
  const [editAsDefault, setEditAsDefault] = useState(false);
  const [editing, setEditing] = useState(false);
  const editLogoRef = useRef(null);

  const title = PAGE_TITLES[location.pathname] || ({
    "/revenue-summary": sectionLabels?.revenue_summary || "Revenue Summary",
    "/revenue-details": sectionLabels?.revenue_details || "Revenue Details",
    "/business-tracker": sectionLabels?.business_tracker || "Business Tracker",
  }[location.pathname] || "Global CRT (Client Revenue Tracker)");

  const retryCustomization = () => {
    refreshCustomization().catch(() => {});
  };

  const toggleSidebar = () => {
    setCollapsed((prev) => {
      localStorage.setItem("sidebar_collapsed", prev ? "0" : "1");
      return !prev;
    });
  };

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  // ── Create workspace ─────────────────────────────────────────────────────

  const openCreate = () => {
    setMobileOpen(false);
    setWsTeamCode("");
    setWsClientName("");
    setWsLogo(null);
    setCreateAsDefault(false);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const name = composeWorkspaceName(wsTeamCode, wsClientName);
    if (!wsTeamCode.trim() || !wsClientName.trim()) {
      toast.error("Team Code and Client Name are required.");
      return;
    }
    setCreating(true);
    try {
      const ws = await createWorkspace(name, wsLogo, createAsDefault);
      toast.success(`Workspace "${ws.name}" created and selected`);
      setCreateOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to create workspace"));
    } finally {
      setCreating(false);
    }
  };

  // ── Edit workspace ───────────────────────────────────────────────────────

  const openEdit = useCallback(() => {
    setMobileOpen(false);
    const { teamCode, clientName } = splitWorkspaceName(current.name);
    setEditWsTeamCode(teamCode);
    setEditWsClientName(clientName);
    setEditWsLogo(current.logo || null);
    setEditAsDefault(defaultWorkspaceId === current.id);
    setEditOpen(true);
  }, [current, defaultWorkspaceId]);

  const handleEdit = async () => {
    const name = composeWorkspaceName(editWsTeamCode, editWsClientName);
    if (!editWsTeamCode.trim() || !editWsClientName.trim()) {
      toast.error("Team Code and Client Name are required.");
      return;
    }
    setEditing(true);
    try {
      await updateWorkspace(current.id, name, editWsLogo, editAsDefault);
      toast.success("Workspace updated.");
      setEditOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update workspace"));
    } finally {
      setEditing(false);
    }
  };

  // ── Blocked: no workspace access ─────────────────────────────────────────

  if (user && loaded && workspaces.length === 0) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center p-6" data-testid="no-workspace-access-screen">
        <div className="max-w-md w-full rounded-xl border bg-card shadow-[var(--app-shadow-sm)] p-8 text-center space-y-4">
          <div className="mx-auto h-12 w-12 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 flex items-center justify-center">
            <ShieldAlert className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">No workspace access</h1>
          <p className="text-sm text-muted-foreground" data-testid="no-workspace-access-message">
            Your account ({user?.email}) has not been granted access to any workspace yet.
            Please contact an administrator to request access.
          </p>
          <Button variant="outline" className="rounded-lg" onClick={logout} data-testid="blocked-signout-button">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>
    );
  }

  // ── Main layout ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-dvh bg-background">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:block fixed left-0 top-0 h-dvh border-r bg-card z-40 transition-[width] duration-200 ${
          collapsed ? "w-[4.5rem]" : "w-[16.5rem]"
        }`}
      >
        <SidebarContent onCreateClick={openCreate} onEditClick={openEdit} collapsed={collapsed} />
      </aside>

      <div
        className={`min-h-dvh flex flex-col transition-[padding] duration-200 ${
          collapsed ? "lg:pl-[4.5rem]" : "lg:pl-[16.5rem]"
        }`}
      >
        {/* Topbar */}
        <header className="h-[var(--app-topbar-h)] sticky top-0 z-30 bg-card/80 backdrop-blur border-b flex items-center justify-between px-4 md:px-6 relative">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile drawer trigger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <SidebarContent
                  onNavigate={() => setMobileOpen(false)}
                  onCreateClick={openCreate}
                  onEditClick={openEdit}
                />
              </SheetContent>
            </Sheet>

            <Button
              variant="ghost" size="icon"
              className="hidden lg:inline-flex text-muted-foreground hover:text-foreground"
              data-testid="sidebar-toggle"
              onClick={toggleSidebar}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </Button>
            <h1 className="hidden md:block text-base md:text-lg font-bold text-foreground truncate max-w-[26vw] lg:max-w-none">
              {title}
            </h1>
          </div>

          <div
            data-testid="topbar-brand-text"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[42%] max-w-[32rem] text-center text-[10px] leading-tight font-bold text-foreground sm:text-xs md:text-sm lg:text-base pointer-events-none select-none"
          >
            Global CRT (Client Revenue Tracker)
          </div>

          <div className="flex items-center gap-2">
            {isAdminRole(user?.role) && <ExportButton testId="topbar-export-button" />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="topbar-user-menu"
                  className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium truncate">{user?.name || "User"}</p>
                  <p className="text-xs text-muted-foreground font-normal truncate">{user?.email}</p>
            {isAdminRole(user?.role) && (
                    <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-950/50 border border-teal-200 dark:border-teal-900 rounded-full px-2 py-0.5">
                      {user?.role === "super_admin" ? "Super Admin" : "CXO"}
                    </span>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="user-menu-settings"
                  onClick={() => navigate("/settings")}
                  className="cursor-pointer"
                >
                  <Settings className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={logout}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {customizationError && (
          <div
            className="mx-4 mt-3 md:mx-6 lg:mx-8 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-100"
            role="alert"
            data-testid="customization-error-banner"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-sm">
                  Custom workspace labels could not be loaded. Showing the default names.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0 border-amber-300 bg-transparent text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-900/50"
                onClick={retryCustomization}
                disabled={customizationLoading}
                data-testid="customization-retry-button"
              >
                {customizationLoading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                {customizationLoading ? "Retrying…" : "Retry"}
              </Button>
            </div>
          </div>
        )}

        <main key={current.id} className="flex-1 px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <Outlet />
        </main>
      </div>

      {/* ── Create Workspace dialog ─────────────────────────────────────── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
            <DialogDescription>
              A new workspace starts with the standard service catalog and an empty tracker.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
             <div className="grid grid-cols-2 gap-3">
               <div className="space-y-1.5">
                 <Label htmlFor="workspace-create-team-code">Team Code</Label>
                 <Input
                   id="workspace-create-team-code"
                   data-testid="workspace-create-team-code-input"
                   placeholder="e.g. APAC"
                   value={wsTeamCode}
                   onChange={(e) => setWsTeamCode(normalizeWorkspacePart(e.target.value))}
                   onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                 />
               </div>
               <div className="space-y-1.5">
                 <Label htmlFor="workspace-create-client-name">Client Name</Label>
                 <Input
                   id="workspace-create-client-name"
                   data-testid="workspace-create-client-name-input"
                   placeholder="e.g. ACME"
                   value={wsClientName}
                   onChange={(e) => setWsClientName(normalizeWorkspacePart(e.target.value))}
                   onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                 />
               </div>
             </div>
             <p className="text-xs text-muted-foreground">
               Workspace name: <span className="font-medium text-foreground">
                 {composeWorkspaceName(wsTeamCode, wsClientName) || "TEAM CODE - CLIENT NAME"}
               </span>
             </p>
            <LogoUpload logo={wsLogo} onChange={setWsLogo} inputRef={createLogoRef} />
            <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 cursor-pointer">
              <div>
                <p className="text-sm font-medium">Set as my default workspace</p>
                <p className="text-[11px] text-muted-foreground">This workspace will open for you after sign-in.</p>
              </div>
              <Switch checked={createAsDefault} onCheckedChange={setCreateAsDefault}
                data-testid="workspace-create-default-toggle" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="workspace-create-submit" onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Workspace dialog ───────────────────────────────────────── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Workspace</DialogTitle>
            <DialogDescription>
              Update the name or logo for <span className="font-medium">{current.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
             <div className="grid grid-cols-2 gap-3">
               <div className="space-y-1.5">
                 <Label htmlFor="workspace-edit-team-code">Team Code</Label>
                 <Input
                   id="workspace-edit-team-code"
                   data-testid="workspace-edit-team-code-input"
                   placeholder="e.g. APAC"
                   value={editWsTeamCode}
                   onChange={(e) => setEditWsTeamCode(normalizeWorkspacePart(e.target.value))}
                   onKeyDown={(e) => e.key === "Enter" && handleEdit()}
                 />
               </div>
               <div className="space-y-1.5">
                 <Label htmlFor="workspace-edit-client-name">Client Name</Label>
                 <Input
                   id="workspace-edit-client-name"
                   data-testid="workspace-edit-client-name-input"
                   placeholder="e.g. ACME"
                   value={editWsClientName}
                   onChange={(e) => setEditWsClientName(normalizeWorkspacePart(e.target.value))}
                   onKeyDown={(e) => e.key === "Enter" && handleEdit()}
                 />
               </div>
             </div>
             <p className="text-xs text-muted-foreground">
               Workspace name: <span className="font-medium text-foreground">
                 {composeWorkspaceName(editWsTeamCode, editWsClientName) || "TEAM CODE - CLIENT NAME"}
               </span>
             </p>
            <LogoUpload logo={editWsLogo} onChange={setEditWsLogo} inputRef={editLogoRef} />
            <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 cursor-pointer">
              <div>
                <p className="text-sm font-medium">Set as my default workspace</p>
                <p className="text-[11px] text-muted-foreground">Only one workspace can be your default at a time.</p>
              </div>
              <Switch checked={editAsDefault} onCheckedChange={setEditAsDefault}
                data-testid="workspace-edit-default-toggle" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button data-testid="workspace-edit-submit" onClick={handleEdit} disabled={editing}>
              {editing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
